import { AuthService } from '../../auth/AuthService'
import { Injector } from '../../injector'
import { RestrictionsManager } from '../../RestrictionsManager'
import { UserService } from '../../services/UserService'
import { getStore } from '../Store'
import {
	submitPurchaseRequest,
	readyToSubmitPurchase,
} from './PurchaseActions'
import { UserActionType } from './UserActionTypes'
import { triggerGTMLogin } from '../../ui/cashier/util/GoogleTagManager'
import { RingGameManager } from '../../services/ring-games/RingGameManager'
import { userFeatureService } from '../../services/UserFeatureService'
import { FeatureToggleService } from '../../services/FeatureToggleService'
import { LogManager } from '../../LogManager'

interface PersonalInfoUpdateRequested {
    type: typeof UserActionType.REQUEST_PERSONAL_INFO_UPDATE;
    payload?: undefined
}

interface PersonalInfoUpdated {
    type: typeof UserActionType.PERSONAL_INFO_UPDATED;
    payload?: undefined
}

interface ReceiveUserBalance {
    type: typeof UserActionType.RECEIVE_USER_BALANCE;
    payload?: Balance
}

interface ReceiveUserInfo {
    type: typeof UserActionType.RECEIVE_USER_INFO;
    payload?: UserInfo
}
interface ReceivePersonalInfo {
    type: typeof UserActionType.RECEIVE_PERSONAL_INFO;
    payload?: PersonalInfo
}
interface RequestPersonalInfo {
    type: typeof UserActionType.REQUEST_PERSONAL_INFO;
    payload?: undefined
}
interface ReceiveIdVerificationStatus {
    type: typeof UserActionType.RECEIVE_ID_VERIFICATION_STATUS;
    payload: IdVerificationStatusResponse
}
interface KYCFormUpdated {
    type: typeof UserActionType.KYC_FORM_UPDATED;
    payload: string;
}

interface UserLoginCompleted {
    type: typeof UserActionType.LOGIN_COMPLETED;
    payload?: boolean
}

interface BlockPurchaseUntilRefresh {
	type: typeof UserActionType.BLOCK_PURCHASE_UNTIL_REFRESH;
}

interface ResetBlockPurchaseUntilRefresh {
	type: typeof UserActionType.RESET_BLOCK_PURCHASE_UNTIL_REFRESH;
}


export type UserAction =
    | ReceiveUserBalance
    | ReceiveUserInfo
    | ReceivePersonalInfo
    | RequestPersonalInfo
    | PersonalInfoUpdateRequested
    | PersonalInfoUpdated
    | ReceiveIdVerificationStatus
    | KYCFormUpdated
    | UserLoginCompleted
		| BlockPurchaseUntilRefresh
		| ResetBlockPurchaseUntilRefresh

export function dispatchUserAction(action: UserAction) {
	getStore().dispatch(action)
}

// Action helpers below

export function loginCompleted() {
	dispatchUserAction({ type: UserActionType.LOGIN_COMPLETED })
}

export function triggerLoginCompletedIfApplicable() {
	const state = getStore().getState()
	if (!state.user?.loginCompleted) return

	const userId = state.user.userInfo.userId
	const userEmail = state.user.userInfo.userEmail
	triggerGTMLogin(userId.toString(), userEmail)
}

export function personalInfoUpdateRequested() {
	dispatchUserAction({ type: UserActionType.REQUEST_PERSONAL_INFO_UPDATE })
}

export async function personalInfoUpdated(residentialState: string, isSweepsBundle: Boolean): Promise<void> {
	if (FeatureToggleService.blockMarketFeature.isPromoPlayOnlyMarketsEnabled()) {
		const geolocationState = await AuthService.getUserGeoLocationState()
		if (
			FeatureToggleService.blockMarketFeature.isPromoPlayOnlyMarket(residentialState, geolocationState) &&
			isSweepsBundle
		) {
			userFeatureService.generateAvailableFeatureAccess(residentialState, geolocationState)
			blockPurchaseUntilRefresh()
			throw Error(`An error occurred when submitting personal info due to geolocation mismatch`)
		}
	}
	dispatchUserAction({ type: UserActionType.PERSONAL_INFO_UPDATED })
	if ( readyToSubmitPurchase() ) {
		submitPurchaseRequest()
	}
}

export async function executeLogout() {
	await RingGameManager.onLogout()
	AuthService.logout()
}

export function blockPurchaseUntilRefresh() {
	dispatchUserAction({ type: UserActionType.BLOCK_PURCHASE_UNTIL_REFRESH })
}

export function resetBlockPurchaseUntilRefresh() {
	dispatchUserAction({ type: UserActionType.RESET_BLOCK_PURCHASE_UNTIL_REFRESH })
}

export const fetchUserBalance = (() => {
	let fetching = false
	return async () => {
		if (fetching) return
		fetching = true
		try {
			const balance = await Injector.get(UserService).getUserBalance()
			dispatchUserAction({ type: UserActionType.RECEIVE_USER_BALANCE, payload: balance })
		} catch (e) {
			// Noop
		} finally {
			fetching = false
		}
	}
})()

export const fetchUserInfo = (() => {
	let fetching = false
	return async () => {
		if (fetching) return
		fetching = true
		try {
			const info = await Injector.get(UserService).getUserInfo()
			RestrictionsManager.apply(info)
			dispatchUserAction({ type: UserActionType.RECEIVE_USER_INFO, payload: info })
		} catch (e) {
			// Noop
		} finally {
			fetching = false
		}
	}
})()

export async function isSddDataUpdated(): Promise<boolean> {
	const isSddDataUpdated = await checkKycInfoUpdated()
	if (isSddDataUpdated) return true

	await new Promise(resolve => {setTimeout((resolve), 2000)})

	return await checkKycInfoUpdated()
}

const checkKycInfoUpdated = async (): Promise<boolean> => {
	try {
		const userInfo = await Injector.get(UserService).getUserInfo()
		return userInfo && userInfo.kycInfoUpdated
	} catch (error) {
		LogManager.error(
			{
				message: `Unexpected error occurred while fetching user info after SDD submission for 
				user: [${AuthService.getCurrentUserIdSynchronous()}]: ${error.message}`,
		})

		return false
	}
}

export const fetchPersonalInfo = (() => {
	let fetching = false
	return async () => {
		if (fetching) return
		fetching = true
		try {
			dispatchUserAction({ type: UserActionType.REQUEST_PERSONAL_INFO })
			const personalInfo = await Injector.get(UserService).getPersonalInfo()
			dispatchUserAction({ type: UserActionType.RECEIVE_PERSONAL_INFO, payload: personalInfo })
		} catch (e) {
			// Noop
		} finally {
			fetching = false
		}
	}
})()

export const fetchIdVerificationStatus = (() => {
	let fetching = false
	return async () => {
		if (fetching) return
		fetching = true
		try {
			const idVerificationStatus = await Injector.get(UserService).getUserIdVerificationStatus()
			dispatchUserAction({ type: UserActionType.RECEIVE_ID_VERIFICATION_STATUS, payload: idVerificationStatus })
		} catch (e) {
			// Noop
		} finally {
			fetching = false
		}
	}
})()

// session keep alive
setInterval(fetchUserInfo, 5 * 60 * 1000)
